/**
 * The monthly FC review (the Notion Monthly Meetings page) section by section and row by row, in the page's order, with
 * how each row gets its number. The Monthly tab shows them all as WIP until the numbers are wired in (SSD-7602).
 */

/** How a monthly row is filled. */
export type MonthlyFill = 'AUTO' | 'AUTO_AFTER_SOURCES' | 'COUNTED' | 'OWNER' | 'WAITS_FOR_FF2';

export const MONTHLY_FILL_LABEL: Record<MonthlyFill, string> = {
  AUTO: 'Auto · BigQuery',
  AUTO_AFTER_SOURCES: 'Auto once sources are pulled',
  COUNTED: 'Counted from Slack · Notion · sheets',
  OWNER: 'Written by the owner',
  WAITS_FOR_FF2: 'Waits for an FF2 feature',
};

export const MONTHLY_FILL_LABEL_KO: Record<MonthlyFill, string> = {
  AUTO: '자동 · BigQuery',
  AUTO_AFTER_SOURCES: '원천을 받으면 자동',
  COUNTED: '슬랙 · 노션 · 시트에서 집계',
  OWNER: '담당자가 적음',
  WAITS_FOR_FF2: 'FF2 기능 대기',
};

/** The fill kinds in the order the legend shows them. */
export const FILL_ORDER: MonthlyFill[] = ['AUTO', 'AUTO_AFTER_SOURCES', 'COUNTED', 'OWNER', 'WAITS_FOR_FF2'];

/** A row whose value waits for the finance:read decision, alone or because its section does. */
export const isLocked = (section: MonthlySection, row: MonthlyRow): boolean =>
  section.restricted === true || row.restricted === true;

export interface MonthlyRow {
  label: string;
  labelKo: string;
  fill: MonthlyFill;
  /** Where the number comes from, or who writes it. */
  source: string;
  sourceKo: string;
  /** A people or cost row outside the people and cost sections: shown without a value until finance:read is decided. */
  restricted?: boolean;
}

export interface MonthlySection {
  no: number;
  title: string;
  titleKo: string;
  rows: MonthlyRow[];
  /**
   * Cost and people rows wait for a decision on who may see them (finance:read); until then the section shows its rows
   * without values.
   */
  restricted?: boolean;
}

export const MONTHLY_SECTIONS: MonthlySection[] = [
  {
    no: 1,
    title: "Last month's actions",
    titleKo: '지난달 액션 점검',
    rows: [
      {
        label: "Last month's action items and where each stands",
        labelKo: '지난달 액션과 진행 상황',
        fill: 'COUNTED',
        source: "Last meeting's notes",
        sourceKo: '지난 회의 기록',
      },
    ],
  },
  {
    no: 2,
    title: 'One-page summary',
    titleKo: '한 장 요약',
    rows: [
      {
        label: 'Products inbounded',
        labelKo: '입고 제품',
        fill: 'AUTO',
        source: 'Inbound scans less inbound cancels',
        sourceKo: '입고 스캔 − 입고 취소',
      },
      {
        label: 'Orders inbounded',
        labelKo: '총 입고 오더',
        fill: 'AUTO',
        source: 'Customer orders with an inbound record',
        sourceKo: '입고 기록이 있는 고객 주문',
      },
      { label: 'Orders packed', labelKo: '포장 완료', fill: 'AUTO', source: 'Packing videos', sourceKo: '포장 영상' },
      {
        label: 'Orders handed to carriers',
        labelKo: '월 총 출고 수 (캐리어 인계)',
        fill: 'AUTO',
        source: 'Carrier scans',
        sourceKo: '운송사 스캔',
      },
      {
        label: 'Orders packed per day',
        labelKo: '하루 포장 오더',
        fill: 'AUTO',
        source: 'Average over the days with packing',
        sourceKo: '포장 기록이 있는 날의 평균',
      },
      {
        label: 'Center lead time p90',
        labelKo: '센터 리드타임 p90',
        fill: 'AUTO',
        source: 'Last inbound to first packing',
        sourceKo: '마지막 입고 → 첫 포장',
      },
      {
        label: 'Packed on the day inbound finished',
        labelKo: '입고 끝난 날 포장까지 끝난 비율',
        fill: 'AUTO',
        source: 'Orders whose last inbound fell in the month',
        sourceKo: '마지막 입고가 그달인 오더',
      },
      {
        label: 'Every item in before 15:00, not inbounded that day',
        labelKo: '당일 출고 가능했는데 입고를 못 해 못 나간 오더',
        fill: 'AUTO',
        source: 'Domestic arrival times and inbound records',
        sourceKo: '국내 도착 시각과 입고 기록',
      },
      {
        label: 'No packing signal for 48 hours at month end',
        labelKo: '월말 48시간 넘게 포장 신호 없는 오더',
        fill: 'AUTO',
        source: 'Inbound and packing records',
        sourceKo: '입고·포장 기록',
      },
      {
        label: 'Safety incidents',
        labelKo: '안전 사고',
        fill: 'COUNTED',
        source: 'Slack reports',
        sourceKo: '슬랙 보고',
      },
      {
        label: 'Safety training and checks',
        labelKo: '안전 교육 · 점검',
        fill: 'COUNTED',
        source: 'Fulfillment Planner',
        sourceKo: 'Fulfillment Planner',
      },
    ],
  },
  {
    no: 3,
    title: 'Inbound',
    titleKo: '입고',
    rows: [
      {
        label: 'Every item in that day, not outbounded that day',
        labelKo: '그날 제품이 다 온 오더 중 그날 Outbound 못 한 비율',
        fill: 'AUTO',
        source: 'Arrival times and outbound records',
        sourceKo: '도착 시각과 아웃바운드 기록',
      },
      {
        label: 'The 15:00 measure by weekday and arrival hour',
        labelKo: '15시 기준 지표 요일별 · 도착 시간대별',
        fill: 'AUTO',
        source: 'Arrival times and inbound records',
        sourceKo: '도착 시각과 입고 기록',
      },
      {
        label: 'Door arrival to inbound',
        labelKo: '문 앞 도착 → 입고',
        fill: 'AUTO',
        source: 'Arrival times and inbound records',
        sourceKo: '도착 시각과 입고 기록',
      },
      {
        label: 'Purchase-order inbound by channel',
        labelKo: '사입 입고 (PO 채널별)',
        fill: 'AUTO_AFTER_SOURCES',
        source: 'Procurement Hub Purchase Orders sheet',
        sourceKo: 'Procurement Hub Purchase Orders 시트',
      },
      {
        label: 'Purchase orders not in on the expected day or confirmed late',
        labelKo: '입고 예정일에 안 왔거나 늦게 확인된 PO',
        fill: 'COUNTED',
        source: 'Purchase Orders sheet against center inbound records',
        sourceKo: 'Purchase Orders 시트와 센터 입고 기록',
      },
      {
        label: 'Unrecognized waybills',
        labelKo: '미인식 운송장',
        fill: 'COUNTED',
        source: '#order-unrecognized-tracking posts and [미인식] comments',
        sourceKo: '#order-unrecognized-tracking 게시와 [미인식] 코멘트',
      },
      {
        label: 'Re-ordered: shown delivered, not physically here',
        labelKo: '도착 표시인데 실물 없어 재발주',
        fill: 'OWNER',
        source: 'Order team, with the [재발주] / [REORDER] comment header',
        sourceKo: '오더팀, [재발주] / [REORDER] 머리말 코멘트',
      },
      {
        label: 'Inbound anomalies (damage, short, wrong item)',
        labelKo: '입고 이상 (파손 · 수량 부족 · 오입고)',
        fill: 'COUNTED',
        source: '#order-alert posts and [입고이상] comments',
        sourceKo: '#order-alert 게시와 [입고이상] 코멘트',
      },
      {
        label: 'Inbound cancels',
        labelKo: '입고 취소',
        fill: 'AUTO',
        source: 'Inbound cancel movements',
        sourceKo: '입고 취소 기록',
      },
      {
        label: 'Days over 1,000 units inbound',
        labelKo: '입고 1,000개를 넘긴 날',
        fill: 'AUTO',
        source: 'Inbound movements',
        sourceKo: '입고 기록',
      },
      {
        label: 'Products lost after inbound',
        labelKo: '입고 후 분실 제품',
        fill: 'WAITS_FOR_FF2',
        source: 'FF2 stock-take adjustment reasons',
        sourceKo: 'FF2 실사 조정 사유',
      },
    ],
  },
  {
    no: 4,
    title: 'Outbound · packing · claims',
    titleKo: '출고 · 포장 · 클레임',
    rows: [
      {
        label: 'Outbound to packed',
        labelKo: '아웃바운드 → 포장',
        fill: 'AUTO',
        source: 'Outbound movements and packing videos',
        sourceKo: '아웃바운드 기록과 포장 영상',
      },
      {
        label: 'Packed on the outbound day',
        labelKo: '아웃바운드한 날 포장까지 끝낸 비율',
        fill: 'AUTO',
        source: 'Outbound movements and packing videos',
        sourceKo: '아웃바운드 기록과 포장 영상',
      },
      {
        label: 'No packing signal for 24 · 48 hours at month end',
        labelKo: '월말 24 · 48시간 포장 신호 없음',
        fill: 'AUTO',
        source: 'Outbound movements and packing videos',
        sourceKo: '아웃바운드 기록과 포장 영상',
      },
      {
        label: 'Shipped without a packing video',
        labelKo: '포장 영상 없이 출고된 오더',
        fill: 'AUTO',
        source: 'Carrier scans and packing videos',
        sourceKo: '운송사 스캔과 포장 영상',
      },
      {
        label: 'Orders packed per hour per packing table',
        labelKo: '포장대 1대 시간당 포장 오더',
        fill: 'AUTO',
        source: 'Packing videos',
        sourceKo: '포장 영상',
      },
      {
        label: 'Inbound units per hour with inbound activity',
        labelKo: '입고 기록이 있던 시간당 입고 수량',
        fill: 'AUTO',
        source: 'Inbound movements',
        sourceKo: '입고 기록',
      },
      {
        label: 'Days over 1,400 units outbound',
        labelKo: '출고 1,400개를 넘긴 날',
        fill: 'AUTO',
        source: 'Outbound movements',
        sourceKo: '아웃바운드 기록',
      },
      {
        label: 'Days over rebin 1,312 · safe capacity 1,050',
        labelKo: '리빈 1,312 · 안전처리량 1,050을 넘긴 날',
        fill: 'AUTO',
        source: 'Inbound and outbound movements',
        sourceKo: '입고·아웃바운드 기록',
      },
      {
        label: 'Packing mistakes',
        labelKo: '포장 실수',
        fill: 'COUNTED',
        source: 'CS cases and Slack',
        sourceKo: 'CS 케이스와 슬랙',
      },
      {
        label: 'Damage rate on orders handed to carriers',
        labelKo: '파손율 (운송사 인계 오더 기준)',
        fill: 'AUTO_AFTER_SOURCES',
        source: 'CS Damaged & Missing report',
        sourceKo: 'CS Damaged & Missing 리포트',
      },
      {
        label: 'Claims by type',
        labelKo: '클레임 유형별',
        fill: 'COUNTED',
        source: 'Damaged & Missing report and Gorgias tickets',
        sourceKo: 'Damaged & Missing 리포트와 Gorgias 티켓',
      },
    ],
  },
  {
    no: 5,
    title: 'Stock · returns',
    titleKo: '재고 · 반품',
    rows: [
      {
        label: 'Month-end stock: SKUs and units',
        labelKo: '월말 재고 SKU · 수량',
        fill: 'AUTO_AFTER_SOURCES',
        source: 'Stock export, rolled back by later movements',
        sourceKo: '재고 내보내기에서 그 뒤 입출고를 빼서 역산',
      },
      {
        label: 'Month-end stock cost',
        labelKo: '월말 재고 원가',
        fill: 'AUTO_AFTER_SOURCES',
        source: 'Stock export and the FIFO COGS sheet',
        sourceKo: '재고 내보내기와 FIFO COGS 시트',
      },
      {
        label: 'Stock unchanged for 90 days or more',
        labelKo: '90일 넘게 변동 없는 재고',
        fill: 'AUTO_AFTER_SOURCES',
        source: 'Stock export and movements',
        sourceKo: '재고 내보내기와 입출고 기록',
      },
      {
        label: 'Stock mismatches',
        labelKo: '재고 불일치',
        fill: 'OWNER',
        source: 'Center lead, with the [재고불일치] comment header',
        sourceKo: '센터장, [재고불일치] 머리말 코멘트',
      },
      {
        label: 'Duplicate SKUs left to clean up',
        labelKo: '중복 SKU 정리 남은 수',
        fill: 'OWNER',
        source: 'Category team',
        sourceKo: '카테고리팀',
      },
      {
        label: 'Monthly clean-up of long-unshipped orders',
        labelKo: '장기 미출고 월 1회 정리',
        fill: 'COUNTED',
        source: 'Slack and Fulfillment Planner',
        sourceKo: '슬랙과 Fulfillment Planner',
      },
      {
        label: 'Sample stock level',
        labelKo: '샘플 재고 수준',
        fill: 'COUNTED',
        source: 'Sample work-order sheet',
        sourceKo: '샘플 작업지시서 시트',
      },
      {
        label: 'Location usage',
        labelKo: '로케이션 사용률',
        fill: 'WAITS_FOR_FF2',
        source: 'FF2 locations per SKU',
        sourceKo: 'FF2 SKU별 로케이션',
      },
      {
        label: 'Returns requested · collected · not collected',
        labelKo: '반품 회수 요청 · 회수 · 미회수',
        fill: 'OWNER',
        source: 'Center admin (Return Pending sheet)',
        sourceKo: '센터 관리자 (Return Pending 시트)',
      },
      {
        label: 'Returns waiting in the center',
        labelKo: '센터에 쌓인 반품',
        fill: 'COUNTED',
        source: 'Return Pending sheet',
        sourceKo: 'Return Pending 시트',
      },
    ],
  },
  {
    no: 6,
    title: 'People · productivity',
    titleKo: '인력 · 생산성',
    restricted: true,
    rows: [
      {
        label: 'Headcount by type',
        labelKo: '인원 구성',
        fill: 'COUNTED',
        source: "Center lead's messages",
        sourceKo: '센터장 메시지',
      },
      {
        label: 'Work lines',
        labelKo: '작업 라인',
        fill: 'COUNTED',
        source: "Center lead's messages",
        sourceKo: '센터장 메시지',
      },
      {
        label: 'Short-term worker days',
        labelKo: '단기근로 인일',
        fill: 'COUNTED',
        source: '#fulfillment-finance payment requests',
        sourceKo: '#fulfillment-finance 지급 요청',
      },
      {
        label: 'Regular and contract working hours',
        labelKo: '정규 · 계약직 근무시간',
        fill: 'OWNER',
        source: 'Attendance records',
        sourceKo: '근태 기록',
      },
      {
        label: 'Marketing shipments',
        labelKo: '마케팅 발송',
        fill: 'COUNTED',
        source: '#order-marketing requests',
        sourceKo: '#order-marketing 요청',
      },
    ],
  },
  {
    no: 7,
    title: 'Space · equipment · cost',
    titleKo: '공간 · 설비 · 비용',
    restricted: true,
    rows: [
      {
        label: 'Space and equipment',
        labelKo: '공간 · 설비',
        fill: 'COUNTED',
        source: "Center lead's messages",
        sourceKo: '센터장 메시지',
      },
      {
        label: 'Purchases in the month',
        labelKo: '그달 구매 합계',
        fill: 'COUNTED',
        source: '#fulfillment-finance approval posts',
        sourceKo: '#fulfillment-finance 상신 글',
      },
      {
        label: 'Center running cost',
        labelKo: '센터 운영비',
        fill: 'OWNER',
        source: 'Center admin (monthly expense report)',
        sourceKo: '센터 관리자 (월 지출 보고)',
      },
      {
        label: 'Insurance and weighing review',
        labelKo: '보험 · 무게 측정 재검토',
        fill: 'COUNTED',
        source: 'Finance thread',
        sourceKo: '재무 스레드',
      },
    ],
  },
  {
    no: 8,
    title: 'Next month',
    titleKo: '다음 달 전망',
    rows: [
      {
        label: 'Expected volume',
        labelKo: '예상 물량',
        fill: 'COUNTED',
        source: "Director's forecast against last month's packed orders",
        sourceKo: '디렉터 전망 × 지난달 포장 오더',
      },
      {
        label: 'Holidays and carrier cut-offs',
        labelKo: '공휴일 · 운송사 컷오프',
        fill: 'COUNTED',
        source: '#announcement and the overtime roster',
        sourceKo: '#announcement 와 특근표',
      },
      {
        label: 'Staffing and overtime',
        labelKo: '필요 인력 · 특근',
        fill: 'COUNTED',
        source: "Center lead's messages",
        sourceKo: '센터장 메시지',
        restricted: true,
      },
      { label: 'Big jobs', labelKo: '큰 작업', fill: 'COUNTED', source: 'Campaign weekly', sourceKo: '캠페인 위클리' },
    ],
  },
  {
    no: 9,
    title: 'Operating issues',
    titleKo: '운영 이슈',
    rows: [
      {
        label: 'Issues, at most five',
        labelKo: '운영 이슈 (최대 5개)',
        fill: 'COUNTED',
        source: 'Slack and meeting notes',
        sourceKo: '슬랙과 회의 노트',
      },
      { label: 'Bottleneck ranking', labelKo: '병목 순위', fill: 'COUNTED', source: 'Center lead', sourceKo: '센터장' },
    ],
  },
  {
    no: 10,
    title: "This month's actions",
    titleKo: '이번 달 액션',
    rows: [
      {
        label: 'Actions, owners and due dates',
        labelKo: '액션 · 담당 · 기한',
        fill: 'OWNER',
        source: 'Decided at the meeting',
        sourceKo: '회의에서 정함',
      },
    ],
  },
];

/** How many rows of each fill kind the review holds, for the legend. */
export const monthlyFillCounts = (sections: MonthlySection[] = MONTHLY_SECTIONS): Record<MonthlyFill, number> => {
  const counts: Record<MonthlyFill, number> = {
    AUTO: 0,
    AUTO_AFTER_SOURCES: 0,
    COUNTED: 0,
    OWNER: 0,
    WAITS_FOR_FF2: 0,
  };
  for (const section of sections) {
    for (const row of section.rows) {
      counts[row.fill] += 1;
    }
  }
  return counts;
};
